/**
 * DocContractView - renders a slice of the Protoss document contract
 * (DocumentChild[]) as clean, normalized HTML styled by design tokens.
 *
 * Deliberately NOT a faithful docx render (that is docx-preview's job, used by
 * FilePreviewDialog): it keeps the original CONTENT and STRUCTURE - headings,
 * paragraphs, lists, tables, and inline emphasis/links - but drops the source
 * document's fonts/sizes/colours so a narrow inline preview reads cleanly.
 *
 * Two consumers, two modes:
 *  - DocSectionView (AI chat) passes only `content` (literal docx children, no
 *    `sample`): tokens render literally and deferred blocks are skipped, exactly
 *    as before - byte-identical output.
 *  - The SOW template workbench passes a `sample` (and a `mode`): binding tokens
 *    (`{{protoss:context.clientName}}`), contentBlocks, dynamicBlocks, signature
 *    blocks, TOCs and page breaks are resolved with representative data, or - in
 *    'placeholder' mode - surfaced as labelled chips so the author sees what is
 *    dynamic versus literal.
 */

import type {
  ContentBlockDefinition,
  DocumentChild,
  DynamicBlockDefinition,
  ImageRunDefinition,
  ParagraphChild,
  ParagraphDefinition,
  SignatureBlockDefinition,
  TableCellDefinition,
  TableDefinition,
  TextRunDefinition,
  TocDefinition,
} from '@protoss/doc';
import {
  expandSignatureBlock,
  isDataDrivenSowToken,
  markdownToDocumentChildren,
  whenValueIsTruthy,
} from '@protoss/doc';
import { TokenChip, inferTokenSource } from '@protoss/ui';
import * as React from 'react';
import {
  humanizeSlug,
  resolveTokenText,
  splitTokens,
  type PreviewMode,
  type PreviewSample,
} from './previewSample';

export interface DocHeading {
  text: string;
  level: number;
}

export interface DocContractViewProps {
  content: DocumentChild[];
  /** 'sample' fills binding tokens/blocks with mock data; 'placeholder' labels them. */
  mode?: PreviewMode;
  /** Mock data for resolving tokens/blocks. When omitted, tokens render literally
   *  and deferred blocks are skipped (back-compat for DocSectionView). */
  sample?: PreviewSample;
  /** Heading list used to render a `toc` block (computed by the caller). */
  headings?: DocHeading[];
  /** dynamicBlock dataKeys whose body cells are inline-editable (opt-in; default read-only). */
  editableDataKeys?: Set<string>;
  /** dataKeys currently carrying a manual override (show the "Updated" badge). */
  overriddenKeys?: Set<string>;
  /** dataKeys whose override differs from the deal's current data (drift). */
  driftKeys?: Set<string>;
  /** Full token paths carrying a manual override (show an "Updated" badge; stay highlighted). */
  tokenOverridden?: Set<string>;
  /** Persist the edited rows for a data table as an override (awaited to show a saving spinner). */
  onSaveOverride?: (dataKey: string, rows: Array<Record<string, string>>) => void | Promise<void>;
  /** Clear a data table's override (reset to auto-filled data; awaited for a spinner). */
  onResetOverride?: (dataKey: string) => void | Promise<void>;
  /**
   * Hide the editable table's in-place override bar (Updated badge / hint / reset). Used by the
   * SOW EDITOR, where that status lives on the section heading instead; cells stay editable.
   */
  hideEditBar?: boolean;
  /**
   * Tint plain `table` children with the generated-content highlight (the signature/acceptance
   * table holds client/company name tokens, so it reads like the data tables). Pass it only on a
   * render whose content is just that table (the signature block render), to avoid tinting others.
   */
  tintTables?: boolean;
  /**
   * Workspace brand color (6-char hex, with or without `#`). When set, table
   * headers use it as the fill (white text) and table borders adopt it, matching
   * the branded export. Omit for the default neutral table styling.
   */
  brandColor?: string;
  /**
   * When true, dynamically-generated content (data-driven tokens, data tables,
   * AI-written prose, conditional sections) is yellow-highlighted so users can
   * verify what was auto-generated/computed. Opt-in: only the deal SOW view
   * passes it (template-authoring previews stay un-highlighted).
   */
  highlightGenerated?: boolean;
}

interface RenderCtx {
  mode: PreviewMode;
  sample?: PreviewSample;
  /** Per-row scope for `item.*` tokens inside a dynamicBlock row template. */
  rowScope?: Record<string, string>;
  headings: DocHeading[];
  highlightGenerated?: boolean;
  editableDataKeys?: Set<string>;
  overriddenKeys?: Set<string>;
  driftKeys?: Set<string>;
  tokenOverridden?: Set<string>;
  onSaveOverride?: (dataKey: string, rows: Array<Record<string, string>>) => void | Promise<void>;
  onResetOverride?: (dataKey: string) => void | Promise<void>;
  hideEditBar?: boolean;
  /** Tint plain `table` children (the signature table) with the generated-content highlight. */
  tintTables?: boolean;
}

/** ParagraphChild is a text run when it has no discriminating `type` tag. */
function isTextRun(run: ParagraphChild): run is TextRunDefinition {
  return !('type' in run);
}

/** Clamp a heading level to the 1-4 buckets the preview styles. */
function headingLevel(p: ParagraphDefinition): 1 | 2 | 3 | 4 | undefined {
  if (!p.heading) return undefined;
  if (p.heading === 'Title') return 1;
  const n = Number(p.heading.slice(7));
  if (!Number.isFinite(n)) return undefined;
  return Math.min(4, Math.max(1, n)) as 1 | 2 | 3 | 4;
}

function leafOf(dataKey: string): string {
  return dataKey.split('.').pop() ?? dataKey;
}

/**
 * `when` render guard - the browser twin of `whenIsTruthy` in packages/doc/src/contract-renderer.ts.
 *
 * A child carrying `when` is dropped when its token resolves falsy, so a contract can ship two
 * mutually exclusive variants of a section (the T&M and fixed-fee "Fees and Payments" blocks) and
 * have exactly one survive. Both renderers MUST agree on falsiness or the on-screen SOW and the
 * exported .docx show different contracts.
 *
 * In `mode: 'placeholder'` (template authoring, no deal sample) nothing is gated - the author needs
 * to see every branch of the contract they are editing.
 */
function whenIsTruthy(child: DocumentChild, ctx: RenderCtx): boolean {
  const when = (child as { when?: string }).when;
  if (when === undefined) return true;
  if (!ctx.sample || ctx.mode === 'placeholder') return true;
  const resolved = splitTokens(when)
    .map((seg) =>
      seg.kind === 'text' ? seg.value : resolveTokenText(seg.path, ctx.sample!, ctx.rowScope)
    )
    .join('');
  return whenValueIsTruthy(resolved);
}

/** Render a token-bearing string: literal text, sample values, or token chips. */
function renderText(text: string, ctx: RenderCtx): React.ReactNode {
  if (!text) return null;
  // Back-compat: with no sample, tokens are not meaningful - render literally.
  if (!ctx.sample) return text;
  const segments = splitTokens(text);
  if (segments.length === 1 && segments[0]!.kind === 'text') return text;
  return segments.map((seg, i) => {
    if (seg.kind === 'text') return <React.Fragment key={i}>{seg.value}</React.Fragment>;
    if (ctx.mode === 'placeholder') {
      return <TokenChip key={i} label={seg.label} source={inferTokenSource(seg.path)} />;
    }
    const value = resolveTokenText(seg.path, ctx.sample!, ctx.rowScope);
    const overridden = ctx.tokenOverridden?.has(seg.path) ?? false;
    // Highlight resolved DATA-DRIVEN / computed token values so users can verify
    // what was auto-filled; static tokens (brand/date/config) render plain. An overridden
    // token keeps the highlight (on the value only) and gains an "Updated" badge BESIDE it (outside
    // the highlight, aligned) so the manual edit is visible without the badge dipping below.
    if (ctx.highlightGenerated && (isDataDrivenSowToken(seg.path) || overridden)) {
      const mark = (
        <mark className="dcv-gen" title={overridden ? 'Updated value' : 'Auto-filled from deal data'}>
          {value}
        </mark>
      );
      return overridden ? (
        <span key={i} className="dcv-token-annotated">
          {mark}
          <span className="dcv-token-updated">Updated</span>
        </span>
      ) : (
        <React.Fragment key={i}>{mark}</React.Fragment>
      );
    }
    return <React.Fragment key={i}>{value}</React.Fragment>;
  });
}

/** Wrap text in the semantic emphasis tags the run carries (no inline styling). */
function renderTextRun(run: TextRunDefinition, ctx: RenderCtx, key: React.Key): React.ReactNode {
  if (!run.text) return null;
  let node: React.ReactNode = renderText(run.text, ctx);
  if (node == null) return null;
  if (run.strike) node = <s>{node}</s>;
  if (run.underline) node = <u>{node}</u>;
  if (run.italics) node = <em>{node}</em>;
  if (run.bold) node = <strong>{node}</strong>;
  // A user-applied highlight (TextRunDefinition.highlight, a color NAME). Rendered with a
  // distinct class from the auto-generated `.dcv-gen` so user vs computed highlights differ.
  if (run.highlight) node = <mark className={`dcv-hl-user dcv-hl--${run.highlight}`}>{node}</mark>;
  if (run.link) {
    node = (
      <a href={run.link} target="_blank" rel="noopener noreferrer">
        {node}
      </a>
    );
  }
  // Honor intra-paragraph line breaks (from <br>/hardBreak): the converter sets
  // run.break to the number of breaks preceding this run (e.g. 2 for <br><br>).
  // Without this, multi-line table cells collapse to run-on text on screen.
  if (run.break && run.break > 0) {
    return (
      <React.Fragment key={key}>
        {Array.from({ length: run.break }, (_, b) => (
          <br key={b} />
        ))}
        {node}
      </React.Fragment>
    );
  }
  return <React.Fragment key={key}>{node}</React.Fragment>;
}

/** An image run becomes a small labelled chip (assets can't be resolved client-side). */
function renderImageRun(run: ImageRunDefinition, key: React.Key): React.ReactNode {
  const seg = splitTokens(typeof run.src === 'string' ? run.src : '')[0];
  const label = seg && seg.kind === 'token' ? seg.label : 'Image';
  return <TokenChip key={key} label={label} source="asset" />;
}

function renderInline(run: ParagraphChild, ctx: RenderCtx, key: React.Key): React.ReactNode {
  if (isTextRun(run)) return renderTextRun(run, ctx, key);
  // Image / page-field runs only render in the preview (when a sample is present).
  if (ctx.sample && run.type === 'image') return renderImageRun(run, key);
  return null;
}

function renderRuns(runs: ParagraphChild[], ctx: RenderCtx): React.ReactNode[] {
  return runs.map((run, i) => renderInline(run, ctx, i)).filter((n) => n != null);
}

function renderCell(
  cell: TableCellDefinition,
  ctx: RenderCtx,
  key: React.Key,
  header: boolean
): React.ReactNode {
  const Tag = header ? 'th' : 'td';
  return (
    <Tag
      key={key}
      className={header ? 'ai-doc-section__th' : 'ai-doc-section__td'}
      colSpan={cell.columnSpan}
      rowSpan={cell.rowSpan}
    >
      {renderChildren(cell.children, ctx)}
    </Tag>
  );
}

function renderTable(table: TableDefinition, ctx: RenderCtx, key: React.Key): React.ReactNode {
  // The signature/acceptance table carries data-driven tokens (client/company names) too, so when
  // requested it gets the same yellow generated-content tint as the data tables.
  const wrapClass =
    ctx.tintTables && ctx.highlightGenerated
      ? 'ai-doc-section__table-wrap dcv-gen-table'
      : 'ai-doc-section__table-wrap';
  return (
    <div className={wrapClass} key={key}>
      <table className="ai-doc-section__table">
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri} className="ai-doc-section__tr">
              {row.children.map((cell, ci) => renderCell(cell, ctx, ci, !!row.tableHeader))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function labeledBlock(
  variant: 'ai' | 'table' | 'toc' | 'fixed',
  label: string,
  hint: string | undefined,
  key: React.Key
): React.ReactNode {
  return (
    <div key={key} className={`dcv-block dcv-block--${variant}`}>
      <span className="dcv-block__label">{label}</span>
      {hint ? <span className="dcv-block__hint">{hint}</span> : null}
    </div>
  );
}

/**
 * Wrap a rendered block in a yellow "generated content" band with a tag, for
 * AI-written prose (`source:'ai'`) and/or conditionally-applicable sections. A
 * no-op (just a keyed fragment) when highlighting is off or the block is neither.
 */
function wrapGenerated(
  node: React.ReactNode,
  key: React.Key,
  ctx: RenderCtx,
  opts: { ai?: boolean; conditional?: boolean; conditionalNote?: string }
): React.ReactNode {
  if (!ctx.highlightGenerated || (!opts.ai && !opts.conditional)) {
    return <React.Fragment key={key}>{node}</React.Fragment>;
  }
  const cls = [
    'dcv-gen-section',
    opts.ai && 'dcv-gen-section--ai',
    opts.conditional && 'dcv-gen-section--conditional',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div key={key} className={cls}>
      <div className="dcv-gen-section__tags" aria-hidden="true">
        {opts.ai ? <span className="dcv-gen-section__tag">AI-generated</span> : null}
        {opts.conditional ? (
          <span className="dcv-gen-section__tag" title={opts.conditionalNote}>
            Conditional - verify if this applies
          </span>
        ) : null}
      </div>
      {node}
    </div>
  );
}

function renderContentBlock(
  block: ContentBlockDefinition,
  ctx: RenderCtx,
  key: React.Key
): React.ReactNode {
  // Narrative may be keyed by the full dataKey (`narrative.introduction`, the SOW
  // studio) or by the leaf slug (`introduction`, the deal SOW render context).
  const markdown = ctx.sample?.narrative[block.dataKey] ?? ctx.sample?.narrative[leafOf(block.dataKey)];
  if (ctx.mode === 'sample' && markdown && markdown.trim()) {
    return wrapGenerated(
      renderChildren(
        markdownToDocumentChildren(markdown, { sourceDocument: block.source === 'document' }),
        ctx
      ),
      key,
      ctx,
      {
        ai: block.source === 'ai',
        conditional: block.conditional,
        conditionalNote: block.conditionalNote,
      }
    );
  }
  // No per-deal narrative. A FIXED block with static `default` boilerplate renders that
  // (still as fixed content - NEVER the AI band), so a section that predates its template
  // text still shows for already-generated deals; a deal's own narrative overrides it above.
  if (block.source !== 'ai' && block.default && block.default.trim()) {
    return wrapGenerated(renderChildren(markdownToDocumentChildren(block.default), ctx), key, ctx, {
      ai: false,
      conditional: block.conditional,
      conditionalNote: block.conditionalNote,
    });
  }
  // Still-empty content. In the live deal View (`mode:'sample'`) an empty block - AI OR
  // fixed - renders NOTHING, matching the Word/Google exports. An AI section with no
  // generated content must never surface its internal `narrative.<slug>_ai` dataKey as an
  // "AI-generated" box in the client-facing document. The labeled authoring placeholder is
  // kept only for the studio (`mode:'placeholder'`), tagged by source so a fixed block is
  // never mislabeled AI-generated there.
  if (ctx.mode === 'placeholder') {
    const slugLabel = humanizeSlug(leafOf(block.dataKey));
    return block.source === 'ai'
      ? labeledBlock('ai', `AI-generated: ${slugLabel}`, block.dataKey, key)
      : labeledBlock('fixed', `Fixed content: ${slugLabel}`, block.dataKey, key);
  }
  return <React.Fragment key={key} />;
}

function renderDynamicBlock(
  block: DynamicBlockDefinition,
  ctx: RenderCtx,
  key: React.Key
): React.ReactNode {
  if (ctx.mode === 'placeholder') {
    return labeledBlock('table', `Data table: ${humanizeSlug(leafOf(block.dataKey))}`, block.dataKey, key);
  }
  const rows = ctx.sample?.data[block.dataKey] ?? [];
  if (ctx.editableDataKeys?.has(block.dataKey)) {
    return <EditableDataBlock block={block} ctx={ctx} rows={rows} key={key} />;
  }
  // A data table is entirely data-driven -> highlight the whole table.
  const wrapClass = ctx.highlightGenerated
    ? 'ai-doc-section__table-wrap dcv-gen-table'
    : 'ai-doc-section__table-wrap';
  const table = (
    <div className={wrapClass}>
      <table className="ai-doc-section__table">
        <tbody>
          {(block.headerRows ?? []).map((row, ri) => (
            <tr key={`h${ri}`} className="ai-doc-section__tr">
              {row.children.map((cell, ci) => renderCell(cell, ctx, ci, true))}
            </tr>
          ))}
          {rows.map((row, ri) => (
            <tr key={`r${ri}`} className="ai-doc-section__tr">
              {block.rowTemplate.children.map((cell, ci) =>
                renderCell(cell, { ...ctx, rowScope: row }, ci, false)
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return wrapGenerated(table, key, ctx, {
    conditional: block.conditional,
    conditionalNote: block.conditionalNote,
  });
}

/** The `item.<field>` key a rowTemplate cell binds to (e.g. `{{protoss:item.element}}` → 'element'). */
function itemFieldOf(cell: TableCellDefinition): string | null {
  for (const child of cell.children) {
    if (child.type !== 'paragraph') continue;
    for (const run of child.children) {
      if (isTextRun(run)) {
        const m = run.text.match(/\{\{\s*protoss:item\.([A-Za-z0-9_]+)\s*\}\}/);
        if (m) return m[1] ?? null;
      }
    }
  }
  return null;
}

/**
 * Inline-editable data table. Header rows render read-only (with their colspan/rowspan);
 * body cells are contentEditable. On blur the whole table is collected from the DOM and
 * saved as an override. An "Updated" badge (which is itself the click-to-reset control)
 * appears when overridden.
 */
function EditableDataBlock({
  block,
  ctx,
  rows,
}: {
  block: DynamicBlockDefinition;
  ctx: RenderCtx;
  rows: Record<string, string>[];
}): React.ReactElement {
  const fields = React.useMemo(
    () => block.rowTemplate.children.map((c) => itemFieldOf(c)),
    [block]
  );
  // The values currently displayed (auto-resolved data OR an existing override), normalized the
  // SAME way collectAndSave reads cells back (?? '' then trim). Used to skip a no-op save when the
  // user clicks into a cell and out again without changing anything.
  const baseline = React.useMemo(
    () =>
      JSON.stringify(
        rows.map((row) => {
          const r: Record<string, string> = {};
          // Coerce to string the SAME way the cell renders (a value may arrive as a number at
          // runtime despite the typing); collectAndSave reads it back via textContent (a string).
          fields.forEach((f) => {
            if (f) r[f] = String(row[f] ?? '').trim();
          });
          return r;
        })
      ),
    [rows, fields]
  );
  const tbodyRef = React.useRef<HTMLTableSectionElement>(null);
  const overridden = ctx.overriddenKeys?.has(block.dataKey) ?? false;
  const drift = ctx.driftKeys?.has(block.dataKey) ?? false;
  // True while a cell save / reset is persisting (shows a spinner in the bar + disables Reset).
  const [pending, setPending] = React.useState(false);
  // True when the last save/reset FAILED (shows a retry hint instead of silently reverting).
  const [error, setError] = React.useState(false);

  const collectAndSave = async () => {
    const body = tbodyRef.current;
    if (!body || !ctx.onSaveOverride) return;
    const trs = Array.from(body.querySelectorAll<HTMLTableRowElement>('tr[data-editrow]'));
    const next = trs.map((tr) => {
      const cells = Array.from(tr.querySelectorAll('td'));
      const row: Record<string, string> = {};
      fields.forEach((f, ci) => {
        if (f) row[f] = (cells[ci]?.textContent ?? '').trim();
      });
      return row;
    });
    // Only persist if a cell value actually changed - a focus-then-blur with no edit must not write
    // an override (which would falsely mark the table "Updated" and bump userEditedAt).
    if (JSON.stringify(next) === baseline) return;
    setPending(true);
    setError(false);
    try {
      await ctx.onSaveOverride(block.dataKey, next);
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  };

  const resetOverride = async () => {
    if (!ctx.onResetOverride) return;
    setPending(true);
    setError(false);
    try {
      await ctx.onResetOverride(block.dataKey);
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  };

  // Editable tables stay yellow-highlighted (they are generated data) so an edit reads as
  // "overriding generated content", not replacing it - matching the read-only data tables.
  const wrapClass = `ai-doc-section__table-wrap dcv-editable${
    ctx.highlightGenerated ? ' dcv-gen-table' : ''
  }`;
  return (
    <div className={wrapClass}>
      {/* Compact header line under the section heading: the "Updated" badge (click to reset, like
          the inline token badge) then the editable hint. No "read-only" / boxed bar. Hidden in the
          SOW EDITOR (hideEditBar), where this status lives on the section heading instead. */}
      {!ctx.hideEditBar ? (
        <div className="dcv-editable__bar">
          {overridden ? (
            <button
              type="button"
              className="dcv-editable__badge"
              disabled={pending}
              title="Click to reset to the deal data"
              onClick={() => void resetOverride()}
            >
              Updated{drift ? ' - may not match deal data' : ''}
            </button>
          ) : null}
          {pending ? <span className="dcv-spinner" aria-label="Saving" /> : null}
          {error ? (
            <span className="dcv-editable__error">Save failed - edit again to retry</span>
          ) : null}
          <span className="dcv-editable__hint">edits are saved as an override</span>
        </div>
      ) : null}
      <table className="ai-doc-section__table">
        <tbody ref={tbodyRef}>
          {(block.headerRows ?? []).map((row, ri) => (
            <tr key={`h${ri}`} className="ai-doc-section__tr">
              {row.children.map((cell, ci) => renderCell(cell, ctx, ci, true))}
            </tr>
          ))}
          {rows.map((row, ri) => (
            <tr key={`r${ri}`} data-editrow className="ai-doc-section__tr">
              {fields.map((f, ci) => (
                <td
                  key={ci}
                  className="ai-doc-section__td dcv-editable__cell"
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={collectAndSave}
                >
                  {f ? (row[f] ?? '') : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderSignatureBlock(
  block: SignatureBlockDefinition,
  ctx: RenderCtx,
  key: React.Key
): React.ReactNode {
  // Tint the signature table like the data tables (it holds client/company name tokens).
  return (
    <React.Fragment key={key}>
      {renderChildren(expandSignatureBlock(block), { ...ctx, tintTables: true })}
    </React.Fragment>
  );
}

function renderToc(block: TocDefinition, ctx: RenderCtx, key: React.Key): React.ReactNode {
  const max = block.maxLevel ?? 3;
  const items = ctx.headings.filter((h) => h.level <= max);
  if (ctx.mode === 'placeholder' || items.length === 0) {
    return labeledBlock('toc', typeof block.title === 'string' && block.title ? block.title : 'Table of contents', undefined, key);
  }
  return (
    <div key={key} className="dcv-block dcv-block--toc">
      <span className="dcv-block__label">
        {typeof block.title === 'string' && block.title ? block.title : 'Contents'}
      </span>
      <ol className="ai-doc-section__ol">
        {items.map((h, i) => (
          <li key={i} className="ai-doc-section__li">
            {h.text}
          </li>
        ))}
      </ol>
    </div>
  );
}

function isResultParagraph(p: ParagraphDefinition): boolean {
  for (const child of p.children) {
    if ('text' in child && typeof child.text === 'string') {
      if (/^(result|expected\s+result)\b/i.test(child.text.trim())) return true;
    }
  }
  return false;
}

function renderParagraph(p: ParagraphDefinition, ctx: RenderCtx, key: React.Key): React.ReactNode {
  const runs = renderRuns(p.children, ctx);
  if (runs.length === 0) return null;
  const level = headingLevel(p);
  // Flag a conditionally-applicable heading so users verify whether it applies.
  const condCls = p.conditional && ctx.highlightGenerated ? ' dcv-conditional' : '';
  const condTitle = p.conditional ? (p.conditionalNote ?? 'Conditional - verify if this applies') : undefined;
  const resCls = isResultParagraph(p) ? ' ai-doc-section__p--result' : '';
  if (level) {
    return (
      <p
        key={key}
        className={`ai-doc-section__heading ai-doc-section__heading--l${level}${condCls}`}
        title={condTitle}
      >
        {runs}
      </p>
    );
  }
  return (
    <p key={key} className={`ai-doc-section__p${resCls}${condCls}`} title={condTitle}>
      {runs}
    </p>
  );
}

/** A list item paragraph rendered as <li> (level becomes an indent modifier). */
function renderListItem(p: ParagraphDefinition, ctx: RenderCtx, key: React.Key): React.ReactNode {
  const level = Math.min(3, p.list?.level ?? 0);
  return (
    <li key={key} className={`ai-doc-section__li ai-doc-section__li--l${level}`}>
      {renderRuns(p.children, ctx)}
    </li>
  );
}

/**
 * Render a child list, grouping consecutive list-item paragraphs into one
 * <ul>/<ol> so bullets/numbers render as a real list.
 */
function renderChildren(children: DocumentChild[], ctx: RenderCtx): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let listBuffer: ParagraphDefinition[] = [];
  let listKind: 'bullet' | 'ordered' | null = null;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const items = listBuffer.map((p, i) => renderListItem(p, ctx, i));
    out.push(
      listKind === 'ordered' ? (
        <ol key={`l${out.length}`} className="ai-doc-section__ol">
          {items}
        </ol>
      ) : (
        <ul key={`l${out.length}`} className="ai-doc-section__ul">
          {items}
        </ul>
      )
    );
    listBuffer = [];
    listKind = null;
  };

  for (const child of children) {
    // `when` render guard - must match the docx renderer's `whenIsTruthy` exactly, or the
    // on-screen View and the exported document disagree about which variant of a section
    // (e.g. T&M vs fixed-fee Fees and Payments) the reader sees.
    if (!whenIsTruthy(child, ctx)) continue;
    if (child.type === 'paragraph' && child.list) {
      const kind = child.list.kind;
      if (listKind && kind !== listKind) flushList();
      listKind = kind;
      listBuffer.push(child);
      continue;
    }
    flushList();
    const key = `c${out.length}`;
    if (child.type === 'paragraph') {
      const node = renderParagraph(child, ctx, key);
      if (node) out.push(node);
    } else if (child.type === 'table') {
      out.push(renderTable(child, ctx, key));
    } else if (!ctx.sample) {
      // No sample → this is the literal-only (DocSectionView) path; skip blocks.
      continue;
    } else if (child.type === 'contentBlock') {
      out.push(renderContentBlock(child, ctx, key));
    } else if (child.type === 'dynamicBlock') {
      out.push(renderDynamicBlock(child, ctx, key));
    } else if (child.type === 'signatureBlock') {
      out.push(renderSignatureBlock(child, ctx, key));
    } else if (child.type === 'toc') {
      out.push(renderToc(child, ctx, key));
    } else if (child.type === 'pageBreak') {
      out.push(<hr key={key} className="dcv-pagebreak" aria-hidden />);
    }
    // Unknown block types are skipped.
  }
  flushList();
  return out;
}

export function DocContractView({
  content,
  mode = 'sample',
  sample,
  headings = [],
  editableDataKeys,
  overriddenKeys,
  driftKeys,
  tokenOverridden,
  onSaveOverride,
  onResetOverride,
  hideEditBar,
  tintTables,
  brandColor,
  highlightGenerated,
}: DocContractViewProps) {
  const ctx: RenderCtx = {
    mode,
    sample,
    headings,
    highlightGenerated,
    editableDataKeys,
    overriddenKeys,
    driftKeys,
    tokenOverridden,
    onSaveOverride,
    onResetOverride,
    hideEditBar,
    tintTables,
  };
  const nodes = renderChildren(content, ctx);
  if (nodes.length === 0) return null;
  // Inject only the raw brand color; doc-contract-view.css derives theme-aware table
  // fill/text/border from it (mixing toward --text-primary, which flips per color
  // scheme) so the brand reads well in both light and dark themes.
  const brandStyle = brandColor
    ? ({
        '--ai-doc-brand': brandColor.startsWith('#') ? brandColor : `#${brandColor}`,
      } as React.CSSProperties)
    : undefined;
  return (
    <div className="ai-doc-section" style={brandStyle}>
      {nodes}
    </div>
  );
}
